import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useEffect, useState } from "react";
import {
  ChemicalSampleLocation,
  ChemicalSamplePositionType,
  ProductGroupName,
  TimeAggregation,
} from "src/store/api/generatedApi";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { SelectBox } from "../Selectbox";
import { TableHeader } from "../TableHeader";
import { ToggleSelector } from "../ToggleSelector";
import { FailureRateMetric, GroupedFailureRateSeries } from "../types";
import { FailureRatePlot } from "./FailureRatePlot";
import { FailureRateTable } from "./FailureRateTable";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useDateRange } from "contexts/DateRangeContext";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { useIsAdmin, useTenant } from "hooks/settings";

interface FailureRateTabProps {
  groupedFailureRateSeries: GroupedFailureRateSeries;
}

export const QualityTab = ({
  groupedFailureRateSeries,
}: FailureRateTabProps) => {
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const tenant = useTenant();

  const [selectedTimeAggregation, setSelectedTimeAggregation] =
    useStringQueryParamState(
      "timeAggregation",
      getDefaultTimeAggregation(
        groupedFailureRateSeries.availableTimeAggregations
      )
    );

  const [selectedMetric, setSelectedMetric] =
    useStringQueryParamState<FailureRateMetric>("metric", "failureRate");

  const [selectedSampleLocation, setSelectedSampleLocation] =
    useStringQueryParamState(
      "sampleLocation",
      getDefaultSampleLocation(
        groupedFailureRateSeries.availableSampleLocations
      )
    );

  const [selectedSamplePositionType, setSelectedSamplePositionType] =
    useStringQueryParamState(
      "samplePositionType",
      getDefaultSamplePositionType(
        groupedFailureRateSeries.availableSamplePositionTypes
      )
    );

  const [selectedProductGroup, setSelectedProductGroup] =
    useStringQueryParamState(
      "productGroup",
      getDefaultProductGroup(groupedFailureRateSeries.availableProductGroups)
    );
  const availableChemicalElements = Array.from(
    groupedFailureRateSeries.availableChemicalElementSymbols
  );

  const [selectedChemicalElements, setSelectedChemicalElements] =
    useLiteralListQueryParamState<string>({
      paramName: "chemicalElements",
      permitted: availableChemicalElements,
      localStorageKey: `${tenant}.strategyDashboard.failureRate.selectedChemicalElements`,
      defaultValue: availableChemicalElements,
    });

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    // when one of the select boxes changes, set the default date
    // to the latest date in the series
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on filter change; user also clicks plot
    setSelectedDate(
      getDefaultSelectedDate(
        groupedFailureRateSeries,
        selectedTimeAggregation,
        selectedSampleLocation,
        selectedSamplePositionType,
        selectedProductGroup
      )
    );
  }, [
    groupedFailureRateSeries,
    selectedTimeAggregation,
    selectedSampleLocation,
    selectedSamplePositionType,
    selectedProductGroup,
  ]);

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        {isAdmin ? <QualityExcelButton /> : null}
      </Stack>
      <Grid
        container
        spacing={2}
        sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}
      >
        <Grid>
          <ToggleSelector<TimeAggregation>
            title="frequency"
            selectedValue={selectedTimeAggregation}
            setSelectedValue={setSelectedTimeAggregation}
            availableValueLabels={
              Object.fromEntries(
                Array.from(
                  groupedFailureRateSeries.availableTimeAggregations
                ).map((aggregation) => [aggregation, aggregation])
              ) as Record<TimeAggregation, string>
            }
          />
        </Grid>
        <Grid>
          <ToggleSelector<FailureRateMetric>
            title="metric"
            selectedValue={selectedMetric}
            setSelectedValue={setSelectedMetric}
            availableValueLabels={{
              failureRate: "failureRate",
              numFailures: "numFailures",
            }}
          />
        </Grid>
        <Grid>
          <SelectBox
            availableOptions={Array.from(
              groupedFailureRateSeries.availableSampleLocations
            )}
            selectedOption={selectedSampleLocation}
            onOptionChange={setSelectedSampleLocation}
            id="sample-location-select"
            labelId="sample-location-label"
            label={t("sampleLocation")}
          />
        </Grid>
        <Grid>
          <SelectBox
            availableOptions={Array.from(
              groupedFailureRateSeries.availableSamplePositionTypes
            )}
            selectedOption={selectedSamplePositionType}
            onOptionChange={setSelectedSamplePositionType}
            id="sample-position-type-select"
            labelId="sample-position-type-label"
            label={t("samplePositionType")}
          />
        </Grid>
        <Grid>
          <SelectBox
            availableOptions={groupedFailureRateSeries.availableProductGroups}
            selectedOption={selectedProductGroup}
            onOptionChange={setSelectedProductGroup}
            id="product-group-select"
            labelId="product-group-label"
            label={t("productGroup")}
          />
        </Grid>
        <Grid>
          <MultiSelect<string, string>
            sx={{ minWidth: 240 }}
            options={availableChemicalElements}
            getOptionId={(element) => element}
            getOptionLabel={(element) => element}
            values={selectedChemicalElements}
            setValues={setSelectedChemicalElements}
            small
            selectAll
            labelAbove
            label={t("chemicalElements")}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 6 }} ml={0}>
          <FailureRatePlot
            failureRateSeries={groupedFailureRateSeries}
            selectedTimeAggregation={selectedTimeAggregation}
            selectedMetric={selectedMetric}
            selectedSampleLocation={selectedSampleLocation}
            selectedSamplePositionType={selectedSamplePositionType}
            selectedProductGroup={selectedProductGroup}
            selectedChemicalElements={selectedChemicalElements}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TableHeader
            titlePrefix={t("failuresFrom")}
            selectedDate={selectedDate}
            formatDateMonthly={selectedTimeAggregation === "daily"}
            subtitleText={t("failureRateTableSubtitleText")}
          />
          <FailureRateTable
            groupedFailureRateSeries={groupedFailureRateSeries}
            selectedDate={selectedDate}
            selectedTimeAggregation={selectedTimeAggregation}
            selectedSampleLocation={selectedSampleLocation}
            selectedSamplePositionType={selectedSamplePositionType}
            selectedProductGroup={selectedProductGroup}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const QualityExcelButton = () => {
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardFailureRate.useLazyQuery();
  const start = startDate.format();
  const end = endDate.format();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};

const getDefaultTimeAggregation = (
  availableTimeAggregations: Set<TimeAggregation>
) => {
  return availableTimeAggregations.has("daily")
    ? "daily"
    : (Array.from(availableTimeAggregations)[0] ?? null);
};

const getDefaultSampleLocation = (
  availableSampleLocations: Set<ChemicalSampleLocation>
) => {
  return Array.from(availableSampleLocations)[0] ?? null;
};

const getDefaultSamplePositionType = (
  availableSamplePositionTypes: Set<ChemicalSamplePositionType>
) => {
  return Array.from(availableSamplePositionTypes)[0] ?? null;
};

const getDefaultProductGroup = (availableProductGroups: ProductGroupName[]) => {
  return availableProductGroups.includes("all")
    ? "all"
    : (availableProductGroups[0] ?? null);
};

const getDefaultSelectedDate = (
  groupedFailureRateSeries: GroupedFailureRateSeries,
  selectedTimeAggregation: TimeAggregation | null,
  selectedSampleLocation: ChemicalSampleLocation | null,
  selectedSamplePositionType: ChemicalSamplePositionType | null,
  selectedProductGroup: ProductGroupName | null
) => {
  const defaultSelectedDateString = groupedFailureRateSeries.values
    .filter(
      (item) =>
        item[0].timeAggregation == selectedTimeAggregation &&
        item[0].sampleLocation == selectedSampleLocation &&
        item[0].samplePositionType == selectedSamplePositionType &&
        item[0].productGroup == selectedProductGroup
    )
    .map((item) => item[1].date[item[1].date.length - 1] ?? null)
    .reduce((max, current) => {
      if (max === null) {
        return current;
      }
      if (current === null) {
        return max;
      }
      return current > max ? current : max;
    }, null);
  return defaultSelectedDateString
    ? new Date(Date.parse(defaultSelectedDateString))
    : null;
};
